import React from "react";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { Layout } from "../../shared/components/Layout";
import { useQueryEmployeeTaxFormSchema } from "../../query/employeeTaxForms/queries/useQueryEmployeeTaxFormSchema";
import {
  PATH_EMPLOYEE_TAX_FORMS_FORM,
  routeHelpersEmployeeTaxForms,
  routeHelpersEmployeeTaxFormsForm,
  routeHelpersEmployeeTaxFormsFormResult
} from "./paths";
import { EmployeeTaxFormRenderer } from "./routes/EmployeeTaxFormRenderer";
import { EmployeeTaxFormReview } from "./routes/EmployeeTaxFormReview";
import { EmployeeTaxFormsChecklist } from "./routes/EmployeeTaxFormsChecklist";

// Standalone entry point (reached from the Requirements page and the onboarding
// tax-forms route). The shared Layout supplies the centered, padded content
// column + header/back so the route components don't render full-bleed. The
// onboarding flow renders these route components directly as steps (already
// wrapped by OnboardingFlow's Layout), so this wrapper is scoped to the
// standalone route only.
export const EmployeeTaxFormsModule: React.FC = () => {
  const history = useHistory();

  // On a specific form (or its result) show the form's name in the header
  // instead of the generic module title. The schema query is keyed by
  // formInstanceId and shared with the form route's own query (React Query
  // dedupes), so this adds no extra request; on the checklist route there is
  // no instance id, the query is disabled, and the title falls back to the
  // generic one.
  const formMatch = useRouteMatch<{ formInstanceId: string }>(
    PATH_EMPLOYEE_TAX_FORMS_FORM
  );
  const formInstanceId = formMatch?.params.formInstanceId ?? "";
  const querySchema = useQueryEmployeeTaxFormSchema(formInstanceId);
  const title = querySchema.data?.title ?? "Tax form requirement";

  // The form (renderer) route gets the wide 944px content column so the
  // wizard's rail + content canvas genuinely renders at 944px (the renderer
  // only caps its width with maxWidth, which can't widen past the host
  // column). `formMatch` is a non-exact match on the form path, so isExact is
  // true only on the renderer route — the checklist and result routes keep
  // the default 760px column they were designed at.
  const isFormRoute = Boolean(formMatch?.isExact);

  return (
    <Layout
      title={title}
      onClose={() => history.goBack()}
      wideContent={isFormRoute}
    >
      <Switch>
        <Route
          exact
          path={routeHelpersEmployeeTaxFormsFormResult.path}
          component={EmployeeTaxFormReview}
        />
        <Route
          exact
          path={routeHelpersEmployeeTaxFormsForm.path}
          component={EmployeeTaxFormRenderer}
        />
        <Route
          exact
          path={routeHelpersEmployeeTaxForms.path}
          component={EmployeeTaxFormsChecklist}
        />
      </Switch>
    </Layout>
  );
};
